import React from "react";
import { MapPin, Tractor, Hammer, House, Wind, ShieldCheck, Siren, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const serviceAreas = [];
const specializedServices = [{
  title: "Domestic Electrical",
  description: "Comprehensive home wiring, socket installations, and safety upgrades tailored for modern living standards.",
  icon: House
}, {
  title: "Commercial Work",
  description: "High-capacity power distribution and lighting solutions for offices, retail spaces, and warehouses.",
  icon: Hammer
}, {
  title: "EV Charger Install",
  description: "Professional installation of smart electric vehicle charging points for homes and business fleets.",
  icon: Wind
}, {
  title: "Fuse Board Upgrades",
  description: "Modern consumer unit replacements with RCD protection to ensure your property meets current safety regulations.",
  icon: ShieldCheck
}, {
  title: "Emergency Callouts",
  description: "Rapid 24/7 response for power failures, electrical faults, and urgent safety hazards in your property.",
  icon: Siren
}, {
  title: "Inspections & Testing",
  description: "Detailed EICR reports and PAT testing to verify the safety and compliance of your electrical systems.",
  icon: Tractor
}];
const serviceListItems = ["Domestic Electrical Services", "Commercial Electrical Services", "Agricultural Electrical Services", "PAT Testing", "Energy Retrofit Works", "Refurbishments & Renovations", "Electrical Maintenance & Repairs", "Fault Finding & Diagnostics", "Lighting & Power Installations"];
export default function SpecializedServicesGrid() {
  return <section data-section-id="256962" id="our-electrical-solutions" className="w-full flex flex-col bg-background text-foreground">
      
      <div className="bg-background py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            <div className="flex flex-col relative">
              <div className="relative rounded-lg overflow-hidden shadow-lg border border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/DSC06327.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/ad_electrical_technician_at_work.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1678780/img/ad_electrical_technician_at_work.png-wvc-sizes" alt="Professional electrician installing a modern fuse board" className="w-full object-cover aspect-[4/3] !h-[400px]" loading="lazy" />
              </div>
              
              <div className="relative mt-4 self-end md:absolute md:bottom-6 md:right-6 bg-ring text-white p-5 rounded-lg shadow-xl max-w-[200px]">
                <div className="font-default text-3xl font-bold mb-1">5+</div>
                <div className="text-sm font-medium leading-tight opacity-90">
                  Years of Technical Excellence
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight tracking-tight">
                Anthony Dobbs Electrical
              </h2>
              
              <div className="space-y-4 text-muted-foreground text-lg leading-relaxed font-default">
                <p className="font-semibold text-foreground">Reliable Electrical Services for Domestic, Commercial & Agricultural Clients</p>
                <p>
                  Anthony Dobbs Electrical provides professional, dependable electrical services across domestic, commercial, and agricultural sectors. With a strong focus on quality workmanship, safety, and customer satisfaction, we deliver everything from small electrical repairs and installations to large-scale projects and energy‑efficient upgrades.
                </p>
                <p>
                  Our services include electrical installations, maintenance, fault finding, PAT testing, energy retrofit works, refurbishments, lighting solutions, rewiring, and electrical upgrades for homes, businesses, farms, and industrial premises.
                </p>
                <p>
                  Whether you're building, renovating, upgrading your property's energy efficiency, or simply need a trusted electrician, Anthony Dobbs Electrical offers expert solutions tailored to your needs.
                </p>
                <p className="font-medium">
                  Professional. Reliable. Fully Committed to Quality. Contact Anthony Dobbs Electrical today for a free quote or to discuss your project requirements.
                </p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 pt-4">
                  {serviceListItems.map((item, i) => <li key={i} data-index={i} className="flex items-center gap-2 text-base">
                      <CheckCircle2 className="size-4 text-ring shrink-0" />
                      <span>{item}</span>
                    </li>)}
                </ul>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                {serviceAreas.map((area, i) => <div key={i} data-index={i} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent text-ring text-sm font-mono font-medium border border-ring/20">
                    <MapPin className="size-4" />
                    {area}
                  </div>)}
              </div>
            </div>

          </div>
        </div>
      </div>

      <div className="bg-secondary py-16 md:py-24 border-t border-border">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16 flex flex-col items-center gap-4">
            <span className="text-ring font-mono font-semibold tracking-widest text-sm uppercase">
              Our Expertise
            </span>
            <h2 className="font-default text-3xl md:text-4xl font-bold text-foreground tracking-tight">
              Comprehensive Electrical Solutions
            </h2>
            <p className="text-muted-foreground text-lg font-default">
              Reliable, certified, and professional services tailored to your specific power requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {specializedServices.map((service, i) => {
            const Icon = service.icon;
            return <Card key={i} data-index={i} className="border border-border shadow-sm hover:shadow-lg hover:border-b-4 hover:border-b-ring transition-all duration-300 bg-card group">
                  <CardHeader className="pb-4">
                    <div className="size-10 rounded-md bg-accent text-ring flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="font-default text-xl font-bold">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed text-muted-foreground font-default">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>;
          })}
          </div>

        </div>
      </div>
    </section>;
}